// src/components/AdminPanel/PublicHolidays.js
import React, { useState, useEffect, useCallback } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import LoadingAnimation from "../LoadingAnimation";

// ─── FIRESTORE REF ───────────────────────────────────────────────────────────
const COLLECTION = "settings";
const DOC_ID = "publicHolidays";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const toDisplayDate = (iso) => {
  // iso = "YYYY-MM-DD"
  const [year, month, day] = iso.split("-");
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const todayIso = () => new Date().toISOString().split("T")[0];

// ─── COMPONENT ───────────────────────────────────────────────────────────────
const PublicHolidays = () => {
  const [holidays, setHolidays] = useState([]); // ["YYYY-MM-DD", ...]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickedDate, setPickedDate] = useState("");
  const [note, setNote] = useState("");


  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const ref = doc(db, COLLECTION, DOC_ID);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        const parsedHolidays = (Array.isArray(data.dates) ? data.dates : []).map(item =>
          typeof item === "string" ? { date: item, note: "" } : item
        );
        setHolidays(parsedHolidays);
      } else {
        setHolidays([]);
      }
    } catch (err) {
      console.error("Failed to fetch public holidays:", err);
      toast.error("Failed to load public holidays.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  // ── Add ────────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!pickedDate) {
      toast.warn("Please select a date first.");
      return;
    }

    const selectedDateObj = new Date(pickedDate);
    const dayOfWeek = selectedDateObj.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      toast.error("Saturdays and Sundays are already excluded.");
      return;
    }
    if (holidays.some(h => h.date === pickedDate)) {
      toast.info("This date is already marked as a public holiday.");
      return;
    }
    const updated = [...holidays, { date: pickedDate, note }].sort((a, b) => a.date.localeCompare(b.date));
    setHolidays(updated);
    setPickedDate("");
    setNote("");

    try {
      const ref = doc(db, COLLECTION, DOC_ID);
      await setDoc(ref, { dates: updated, updatedAt: new Date() }, { merge: true });
      toast.success(`${toDisplayDate(pickedDate)} added as public holiday.`);
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to add holiday. Please try again.");
    }
  };

  // ── Remove ─────────────────────────────────────────────────────────────────
  const handleRemove = async (dateIso) => {
    const updated = holidays.filter((h) => h.date !== dateIso);
    setHolidays(updated);

    try {
      const ref = doc(db, COLLECTION, DOC_ID);
      await setDoc(ref, { dates: updated, updatedAt: new Date() }, { merge: true });
      toast.info(`${toDisplayDate(dateIso)} removed from holidays.`);
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to remove holiday. Please try again.");
    }
  };

  // ── Categorise ────────────────────────────────────────────────────────────
  const today = todayIso();
  const upcoming = holidays.filter((h) => h.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = holidays.filter((h) => h.date < today).sort((a, b) => b.date.localeCompare(a.date));

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#fff", borderRadius: "10px", boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)" }}>
      <LoadingAnimation text="Loading public holidays..." />
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="postjob-container">

      {/* ── PAGE HEADER ── */}
      <h2 className="page-heading">
        <i className="fas fa-umbrella-beach" style={{ color: "#FF8000" }}></i>
        Public Holidays
      </h2>

      {/* ── INFO BANNER ── */}
      <div style={styles.infoBanner}>
        <i className="fas fa-info-circle" style={{ color: "#FF8000", marginRight: 10 }}></i>
        <span>
          Dates marked here will <strong>prevent users from submitting</strong> job or
          reservation applications. A polite notice will be displayed on the
          application pages.
        </span>
      </div>

      {/* ── ADD DATE CARD ── */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          <i className="fas fa-calendar-plus" style={{ color: "#FF8000", marginRight: 10 }}></i>
          Add a Public Holiday
        </h3>

        <div style={styles.addRow}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Select Date</label>
            <input
              type="date"
              value={pickedDate}
              min={today}
              onChange={(e) => setPickedDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Note (optional)</label>
            <input
              type="text"
              placeholder="e.g. Eid ul-Adha"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={styles.textInput}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
          </div>

          <button
            onClick={handleAdd}
            style={styles.addBtn}
            title="Add holiday"
          >
            <i className="fas fa-plus"></i> Add
          </button>
        </div>
      </div>

      {/* ── UPCOMING HOLIDAYS ── */}
      <div style={styles.card}>
        <div style={styles.cardTitleRow}>
          <h3 style={styles.cardTitle}>
            <i className="fas fa-calendar-day" style={{ color: "#FF8000", marginRight: 10 }}></i>
            Upcoming & Active Holidays
          </h3>
          <span style={styles.badge}>{upcoming.length}</span>
        </div>

        {upcoming.length === 0 ? (
          <div style={styles.emptyState}>
            <i className="fas fa-calendar-check" style={{ fontSize: 40, color: "#ccc", marginBottom: 12 }}></i>
            <p style={{ color: "#888", margin: 0 }}>No upcoming public holidays configured.</p>
          </div>
        ) : (
          <div style={styles.listGrid}>
            {upcoming.map((item) => {
              const isToday = item.date === today;
              return (
                <div
                  key={item.date}
                  style={{
                    ...styles.holidayCard,
                    borderLeftColor: isToday ? "#FF8000" : "#e2e8f0",
                    background: isToday ? "#fff8f0" : "#fff",
                  }}
                >
                  <div style={styles.holidayLeft}>
                    <span style={{ ...styles.holidayIcon, background: isToday ? "#FF8000" : "#f1f5f9" }}>
                      <i
                        className={isToday ? "fas fa-star" : "fas fa-umbrella-beach"}
                        style={{ color: isToday ? "#fff" : "#FF8000", fontSize: 14 }}
                      ></i>
                    </span>
                    <div>
                      <p style={styles.holidayDate}>
                        {toDisplayDate(item.date)}
                        {item.note && <span style={{ marginLeft: 8, fontWeight: 400, color: "#64748b", fontSize: 13 }}>- {item.note}</span>}
                      </p>
                      {isToday && (
                        <span style={styles.todayBadge}>Today — Applications Blocked</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(item.date)}
                    style={styles.removeBtn}
                    title="Remove holiday"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── PAST HOLIDAYS ── */}
      {past.length > 0 && (
        <div style={{ ...styles.card, opacity: 0.7 }}>
          <details>
            <summary style={{ ...styles.cardTitle, cursor: "pointer", userSelect: "none" }}>
              <i className="fas fa-history" style={{ color: "#aaa", marginRight: 10 }}></i>
              Past Holidays ({past.length})
            </summary>
            <div style={{ marginTop: 16 }}>
              {past.map((item) => (
                <div key={item.date} style={{ ...styles.holidayCard, opacity: 0.6 }}>
                  <div style={styles.holidayLeft}>
                    <span style={{ ...styles.holidayIcon, background: "#f1f5f9" }}>
                      <i className="fas fa-clock" style={{ color: "#aaa", fontSize: 14 }}></i>
                    </span>
                    <p style={{ ...styles.holidayDate, color: "#888" }}>
                      {toDisplayDate(item.date)}
                      {item.note && <span style={{ marginLeft: 8, fontWeight: 400, color: "#94a3b8", fontSize: 13 }}>- {item.note}</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(item.date)}
                    style={{ ...styles.removeBtn, background: "#f1f5f9", color: "#aaa" }}
                    title="Remove past holiday"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}



    </div>
  );
};

// ─── INLINE STYLES ────────────────────────────────────────────────────────────
const styles = {
  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    background: "#fff8f0",
    border: "1px solid #ffd199",
    borderRadius: 12,
    padding: "14px 18px",
    marginBottom: 24,
    fontSize: 14,
    color: "#7a4000",
    lineHeight: 1.6,
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 24,
    marginBottom: 24,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  cardTitle: {
    margin: "0 0 18px",
    fontSize: 16,
    fontWeight: 700,
    color: "#1a202c",
    display: "flex",
    alignItems: "center",
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  badge: {
    background: "#FF8000",
    color: "#fff",
    borderRadius: "999px",
    padding: "2px 10px",
    fontSize: 13,
    fontWeight: 700,
  },
  addRow: {
    display: "flex",
    gap: 16,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 180,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#4a5568",
    marginBottom: 6,
  },
  dateInput: {
    padding: "11px 14px",
    borderRadius: 8,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
  },
  textInput: {
    padding: "11px 14px",
    borderRadius: 8,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#FF8000",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "11px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    alignSelf: "flex-end",
    boxShadow: "0 2px 6px rgba(255,128,0,0.3)",
    transition: "all 0.2s",
  },
  listGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  holidayCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #e2e8f0",
    transition: "box-shadow 0.2s",
  },
  holidayLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  holidayIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  holidayDate: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#1a202c",
  },
  todayBadge: {
    display: "inline-block",
    marginTop: 4,
    background: "#FF8000",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: "999px",
    padding: "2px 10px",
    letterSpacing: "0.3px",
  },
  removeBtn: {
    background: "#fff0f0",
    border: "none",
    borderRadius: 8,
    color: "#e53e3e",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.2s",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    textAlign: "center",
  },
  saveBtn: {
    display: "flex",
    alignItems: "center",
    background: "#FF8000",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "12px 28px",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(255,128,0,0.3)",
    transition: "all 0.2s",
  },
  cancelBtn: {
    display: "flex",
    alignItems: "center",
    background: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    borderRadius: 8,
    padding: "12px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default PublicHolidays;
